import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Select,
  Skeleton,
  Button,
  Pagination,
} from '@mantine/core';
import { IconPackage } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import { useOrders } from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PAGE_SIZE = 20;

export default function OrderHistory() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useOrders({
    status: status || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const orders = data?.items ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  return (
    <Stack gap="lg">
      <PageHeader
        title="My Orders"
        description="View and manage your order history"
        actions={
          <Select
            placeholder="Filter by status"
            data={STATUS_OPTIONS}
            value={status}
            onChange={(v) => { setStatus(v ?? ''); setPage(1); }}
            w={180}
            size="sm"
            clearable
          />
        }
      />

      {isLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : orders.length === 0 ? (
        <EmptyState
          icon={<IconPackage size={30} />}
          title="No orders found"
          description={status ? 'Try a different filter' : 'Place your first order from our catalog'}
          action={
            !status ? (
              <Button component={Link} to="/shop">
                Browse Catalog
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Order #</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Total</Table.Th>
                  <Table.Th w={100} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {orders.map((order) => (
                  <Table.Tr key={order.id}>
                    <Table.Td>
                      <Text
                        size="sm"
                        fw={500}
                        c="brand"
                        component={Link}
                        to={`/orders/${order.id}`}
                        style={{ textDecoration: 'none' }}
                      >
                        {order.order_number}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{dayjs(order.created_at).format('MMM D, YYYY')}</Text>
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={order.status} />
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" fw={500}>${parseFloat(order.total).toFixed(2)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Button
                          component={Link}
                          to={`/orders/${order.id}`}
                          variant="subtle"
                          size="xs"
                        >
                          View
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && (
            <Group justify="center">
              <Pagination total={totalPages} value={page} onChange={setPage} />
            </Group>
          )}
        </>
      )}
    </Stack>
  );
}
